import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { 
  FiUsers, FiBox, FiDollarSign, FiTrendingUp, FiActivity, FiClock, FiArrowRight, 
  FiCalendar
} from 'react-icons/fi';
import { patientService, inventoryService, paymentService } from '../../services/api';
import { 
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';

const AdminDashboard = () => {
  const [data, setData] = useState({
    patients: [],
    inventory: [],
    payments: []
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [patients, inventory, payments] = await Promise.all([
          patientService.getAll(),
          inventoryService.getAll(),
          paymentService.getAll()
        ]);
        setData({
          patients,
          inventory,
          payments
        });
      } catch (error) {
        console.error('Dashboard synchronization error', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Advanced Analytics Processing
  const analytics = useMemo(() => {
    const now = new Date();
    const todayStr = now.toISOString().split('T')[0];
    const monthStr = todayStr.substring(0, 7);
    const yearStr = todayStr.substring(0, 4);

    // Revenue Calculations
    const dailyIncome = data.payments
      .filter(p => p.payment_date.startsWith(todayStr))
      .reduce((acc, curr) => acc + parseFloat(curr.amount), 0);

    const monthlyIncome = data.payments
      .filter(p => p.payment_date.startsWith(monthStr))
      .reduce((acc, curr) => acc + parseFloat(curr.amount), 0);

    const yearlyIncome = data.payments
      .filter(p => p.payment_date.startsWith(yearStr))
      .reduce((acc, curr) => acc + parseFloat(curr.amount), 0);

    const totalIncome = data.payments.reduce((acc, curr) => acc + parseFloat(curr.amount), 0);

    // Patients Chart Processing (Last 14 Days)
    const last14Days = [...Array(14)].map((_, i) => {
      const d = new Date();
      d.setDate(d.getDate() - (13 - i));
      return d.toISOString().split('T')[0];
    });

    const patientChartData = last14Days.map(date => ({
      date: new Date(date).toLocaleDateString('en-US', { day: 'numeric', month: 'short' }),
      count: data.patients.filter(p => p.created_at.startsWith(date)).length,
      income: data.payments
        .filter(p => p.payment_date.startsWith(date))
        .reduce((acc, curr) => acc + parseFloat(curr.amount), 0)
    }));

    return {
      dailyIncome,
      monthlyIncome,
      yearlyIncome,
      totalIncome,
      patientChartData,
      recentPatients: data.patients.slice(0, 5)
    };
  }, [data]);

  const statCards = [
    { name: 'Daily Income', value: analytics.dailyIncome, icon: <FiClock />, color: 'bg-teal-500', secondary: 'Today' },
    { name: 'Monthly Target', value: analytics.monthlyIncome, icon: <FiCalendar />, color: 'bg-indigo-600', secondary: 'This Month' },
    { name: 'Annual Revenue', value: analytics.yearlyIncome, icon: <FiDollarSign />, color: 'bg-slate-900', secondary: 'Full Year' },
    { name: 'Stock Assets', value: data.inventory.length, icon: <FiBox />, color: 'bg-white', secondary: 'Inventory' },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
         <div className="flex flex-col items-center space-y-4">
            <div className="w-12 h-12 border-4 border-slate-200 border-t-indigo-600 rounded-full animate-spin"></div>
            <p className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-400">Booting Analytics Engine...</p>
         </div>
      </div>
    );
  }

  return (
    <div className="space-y-10 animate-fade-in-down pb-20">
      
      {/* Financial Pulse Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((card, i) => (
          <motion.div
            key={card.name}
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.1 }}
            className={`${card.color.startsWith('bg-white') ? 'bg-white text-slate-900 border border-slate-100' : `${card.color} text-white shadow-xl shadow-indigo-100`} p-8 rounded-xl group cursor-pointer transition-all hover:scale-[1.02] relative overflow-hidden`}
          >
            <div className={`w-12 h-12 rounded-2xl flex items-center justify-center text-xl mb-6 shadow-sm border ${card.color === 'bg-white' ? 'bg-slate-50 border-slate-100 text-slate-900' : 'bg-white/20 border-white/20 text-white'}`}>
              {card.icon}
            </div>
            <p className={`text-[10px] font-black uppercase tracking-[0.2em] ${card.color === 'bg-white' ? 'text-slate-400' : 'text-white/60'}`}>{card.name}</p>
            <div className="flex items-end justify-between mt-2">
               <h3 className="text-2xl font-black">Rs {card.value.toLocaleString()}</h3>
               <span className={`text-[10px] font-bold uppercase ${card.color === 'bg-white' ? 'text-indigo-600' : 'text-white/40'}`}>{card.secondary}</span>
            </div>
            {/* Decorative background circle */}
            <div className={`absolute -right-4 -bottom-4 w-24 h-24 rounded-full bg-white/5 opacity-0 group-hover:opacity-100 transition-all duration-500`}></div>
          </motion.div>
        ))}
      </div>

      {/* Analytics Visualization Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
        
        {/* Patient Traffic Chart */}
        <div className="lg:col-span-2 bg-white rounded-xl p-10 border border-slate-100 shadow-sm flex flex-col">
           <div className="flex items-center justify-between mb-10">
              <div>
                 <h3 className="text-sm font-black uppercase tracking-[0.3em] text-slate-900">Patient Traffic Density</h3>
                 <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">Live Registration Trends (Last 14 Days)</p>
              </div>
              <div className="flex space-x-4">
                 <span className="flex items-center space-x-2 px-3 py-1 bg-indigo-50 text-indigo-600 rounded-lg text-[9px] font-black uppercase tracking-widest leading-none"><FiUsers /><span>Registrations</span></span>
                 <span className="flex items-center space-x-2 px-3 py-1 bg-emerald-50 text-emerald-600 rounded-lg text-[9px] font-black uppercase tracking-widest leading-none"><FiDollarSign /><span>Income</span></span>
              </div>
           </div>
           
           <div className="flex-1 min-h-[300px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                 <AreaChart data={analytics.patientChartData} margin={{ top: 10, right: 0, left: -20, bottom: 0 }}>
                    <defs>
                       <linearGradient id="colorCount" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#6366f1" stopOpacity={0.4}/>
                          <stop offset="95%" stopColor="#6366f1" stopOpacity={0}/>
                       </linearGradient>
                       <linearGradient id="colorIncome" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#10b981" stopOpacity={0.4}/>
                          <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                       </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                    <XAxis 
                       dataKey="date" 
                       axisLine={false} 
                       tickLine={false} 
                       tick={{ fontSize: 10, fontWeight: 900, fill: '#94a3b8' }} 
                       dy={10}
                    />
                    <YAxis 
                       yAxisId="left"
                       axisLine={false} 
                       tickLine={false} 
                       tick={{ fontSize: 9, fontWeight: 900, fill: '#6366f1' }} 
                    />
                    <YAxis 
                       yAxisId="right"
                       orientation="right"
                       axisLine={false} 
                       tickLine={false} 
                       tick={{ fontSize: 9, fontWeight: 900, fill: '#10b981' }} 
                       tickFormatter={(v) => `Rs${v > 999 ? (v/1000).toFixed(0)+'k' : v}`}
                    />
                    <Tooltip 
                       contentStyle={{ borderRadius: '1.25rem', border: 'none', boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1)', padding: '16px' }}
                       labelStyle={{ fontWeight: 900, fontSize: '11px', textTransform: 'uppercase', marginBottom: '8px', color: '#1e293b', letterSpacing: '0.1em' }}
                       itemStyle={{ fontSize: '10px', fontWeight: 800, textTransform: 'uppercase' }}
                    />
                    <Area 
                       yAxisId="left"
                       type="monotone" 
                       dataKey="count" 
                       stroke="#6366f1" 
                       strokeWidth={4} 
                       fillOpacity={1} 
                       fill="url(#colorCount)" 
                       animationDuration={2000}
                    />
                    <Area 
                       yAxisId="right"
                       type="monotone" 
                       dataKey="income" 
                       stroke="#10b981" 
                       strokeWidth={4} 
                       fillOpacity={1} 
                       fill="url(#colorIncome)" 
                       animationDuration={2500}
                    />
                 </AreaChart>
              </ResponsiveContainer>
           </div>
        </div>

        {/* Rapid Deployment Section */}
        <div className="space-y-6">
           <div className="bg-slate-900 rounded-xl p-10 text-white shadow-xl relative overflow-hidden flex flex-col justify-between min-h-[400px]">
              <div className="relative z-10">
                <div className="flex justify-between items-center mb-10">
                   <FiActivity className="text-3xl text-syana-blue-400" />
                   <div className="px-3 py-1 bg-white/10 rounded-full border border-white/10">
                      <span className="text-[8px] font-black uppercase tracking-widest text-syana-blue-400">System Healthy</span>
                   </div>
                </div>
                <h3 className="text-2xl font-bold leading-tight">Patient Statistics Overview</h3>
                <div className="mt-8 space-y-6">
                   <div className="flex items-center justify-between">
                      <p className="text-[10px] font-black uppercase tracking-widest text-white/40">Total Registered</p>
                      <p className="text-xl font-black">{data.patients.length}</p>
                   </div>
                   <div className="w-full bg-white/5 h-2 rounded-full overflow-hidden">
                      <motion.div 
                        initial={{ width: 0 }} 
                        animate={{ width: '85%' }} 
                        className="h-full bg-indigo-500"
                      />
                   </div>
                   <div className="grid grid-cols-2 gap-4 pt-4">
                      <div className="bg-white/5 p-4 rounded-2xl border border-white/5">
                         <p className="text-[8px] font-black text-white/30 uppercase tracking-widest mb-1">Total Sales</p>
                         <p className="text-sm font-black">Rs {analytics.totalIncome.toLocaleString()}</p>
                      </div>
                      <div className="bg-white/5 p-4 rounded-2xl border border-white/5">
                         <p className="text-[8px] font-black text-white/30 uppercase tracking-widest mb-1">Items in Stock</p>
                         <p className="text-sm font-black">{data.inventory.length}</p>
                      </div>
                   </div>
                </div>
              </div>
              
              <div className="relative z-10 pt-10 border-t border-white/10 flex items-center justify-between">
                 <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center">
                       <FiClock className="text-white/60" />
                    </div>
                    <div>
                       <p className="text-[10px] font-black uppercase tracking-widest text-white/30 truncate">Analytics Sync</p>
                       <p className="text-sm font-bold text-white mt-1">Live Feed</p>
                    </div>
                 </div>
                 <FiTrendingUp className="text-indigo-400 text-3xl opacity-20" />
              </div>

              {/* Decorative Blur */}
              <div className="absolute top-[-10%] right-[-10%] w-64 h-64 bg-syana-blue-600/20 blur-[100px]"></div>
           </div>
        </div>
      </div>

      {/* Secondary Row: Recent Registrations Feed */}
      <div className="bg-white rounded-xl p-10 border border-slate-100 shadow-sm max-w-4xl">
        <div className="flex items-center justify-between mb-8">
           <div>
              <h3 className="text-xs font-black uppercase tracking-[0.3em] text-slate-900">Latest Live Registry</h3>
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">Syncing directly from patient dossier</p>
           </div>
           <button onClick={() => window.location.href='/admin/patients'} className="px-6 py-3 bg-slate-50 text-slate-400 rounded-xl flex items-center space-x-3 hover:bg-slate-900 hover:text-white transition-all text-[9px] font-black uppercase tracking-widest">
              <span>View Full Hub</span>
              <FiArrowRight />
           </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {analytics.recentPatients.length === 0 ? (
            <p className="col-span-full text-center py-10 text-slate-300 text-[10px] font-bold uppercase tracking-widest whitespace-nowrap">No live activity detected</p>
          ) : analytics.recentPatients.map((patient) => (
            <div key={patient.id} className="flex flex-col p-6 bg-slate-50 rounded-xl hover:bg-white hover:shadow-xl hover:shadow-slate-100 transition-all border border-transparent hover:border-slate-100 group relative">
              <div className="flex items-center space-x-4 mb-4">
                 <div className="w-10 h-10 bg-white shadow-sm rounded-xl flex items-center justify-center text-slate-900 font-bold group-hover:bg-slate-900 group-hover:text-white transition-all">
                    {patient.name.charAt(0)}
                 </div>
                 <div className="truncate">
                    <p className="font-black text-slate-900 text-[11px] truncate uppercase tracking-tight">{patient.name}</p>
                    <p className="text-[9px] text-slate-400 font-bold uppercase tracking-widest">{patient.reg_no}</p>
                 </div>
              </div>
              <div className="flex items-center justify-between pt-4 border-t border-slate-200/50">
                 <span className="text-[8px] font-black text-slate-300 uppercase tracking-[0.2em]">Joined</span>
                 <span className="text-[10px] font-bold text-slate-900">{new Date(patient.created_at).toLocaleDateString()}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
